import { Sparkles, TrendingUp } from "lucide-react"

import accountsPreview from "~/assets/orbitshift-mobile-accounts-v2.webp"
import signalRadarBackdrop from "~/assets/orbitshift-mobile-signal-radar.webp"
import AppleIcon from "~/components/icons/apple-icon"
import GooglePlayIcon from "~/components/icons/google-play-icon"
import "~/styles/login.css"
import { trackPublic } from "~/utils/amplitude"
import { EVENTS, getBaseEventProps } from "~/utils/amplitude-events"

const APP_STORE_URL = "https://apps.apple.com/in/app/orbitshift-ai/id6480174909"
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=ai.orbitshift.app"

export default function AppDownloadBanner() {
  const handleStoreClick = (store: "app_store" | "google_play") => {
    trackPublic(EVENTS.COMPONENTS.MOBILE_APP_DOWNLOAD_CLICKED, {
      ...getBaseEventProps({ feature: "mobile_app_download_banner" }),
      store,
      placement: "login"
    })
  }

  return (
    <main className="login-left-panel fixed inset-0 z-[10000] flex min-h-[100dvh] items-center justify-center overflow-y-auto bg-[#fafafe] px-4 py-20 text-center md:hidden">
      <div className="login-left-panel-halo" />
      <div className="login-left-panel-grid" />

      <img
        src="/orbitshift_logo.svg"
        alt="OrbitShift"
        className="absolute left-6 top-6 z-10 h-auto w-[140px]"
      />

      <section className="relative z-10 w-full max-w-[380px] overflow-hidden rounded-[28px] bg-white shadow-[0_18px_50px_rgba(35,45,80,0.14)]">
        <div className="relative h-[312px] overflow-hidden bg-primary">
          <img
            src={signalRadarBackdrop}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-[#31267f]/25" />
          <div className="absolute left-3 top-9 z-20 w-28 rounded-2xl border border-white/70 bg-white/80 p-3 text-left shadow-[0_10px_24px_rgba(82,70,170,0.16)] backdrop-blur">
            <div className="flex items-center gap-1.5 text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              <span className="text-[10px] font-semibold">AI signal</span>
            </div>
            <p className="mt-2 text-xs font-bold text-[#3f4855]">High intent</p>
            <p className="mt-0.5 text-[10px] text-[#657082]">Act now</p>
          </div>
          <div className="absolute bottom-9 right-3 z-20 flex w-28 items-center gap-2 rounded-2xl border border-white/70 bg-white/85 p-2.5 text-left shadow-[0_10px_24px_rgba(82,70,170,0.16)] backdrop-blur">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
              <TrendingUp className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-[#3f4855]">
                Priority up
              </p>
              <p className="text-[9px] text-[#657082]">New signal</p>
            </div>
          </div>
          <div className="absolute left-1/2 top-3 z-10 h-[330px] w-[184px] -translate-x-1/2 overflow-hidden rounded-[28px] border-[5px] border-[#242630] bg-[#242630] p-[2px] shadow-[0_18px_30px_rgba(31,35,52,0.32)]">
            <img
              src={accountsPreview}
              alt="OrbitShift accounts dashboard on mobile"
              loading="lazy"
              className="h-full w-full rounded-[20px] object-cover object-top"
            />
            <div className="absolute left-1/2 top-2 h-3 w-16 -translate-x-1/2 rounded-full bg-[#20222a]" />
          </div>
        </div>

        <div className="px-6 pb-7 pt-6">
          <h1 className="text-xl font-bold leading-tight text-[#3f4855]">
            Sales execution, wherever you sell.
          </h1>
          <p className="mx-auto mt-2 max-w-[290px] text-base leading-relaxed text-[#596371]">
            From account priorities and buying signals to knowledge and
            proposals — run your entire sales motion from anywhere.
          </p>

          <div className="mt-6 grid grid-cols-2 gap-2.5">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-app-download-app-store"
              onClick={() => handleStoreClick("app_store")}
              aria-label="Download OrbitShift on the App Store"
              className="flex h-[50px] items-center justify-center gap-2.5 rounded-lg border border-[#a6a6a6] bg-black px-3 text-left text-white transition-transform hover:scale-[1.02] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <AppleIcon className="h-7 w-[22px] shrink-0" />
              <span className="flex flex-col">
                <span className="whitespace-nowrap text-[8px] font-medium leading-none tracking-[0.01em]">
                  Download on the
                </span>
                <span className="mt-1 whitespace-nowrap text-[14px] font-medium leading-none tracking-tight">
                  App Store
                </span>
              </span>
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-app-download-google-play"
              onClick={() => handleStoreClick("google_play")}
              aria-label="Get OrbitShift on Google Play"
              className="flex h-[50px] items-center justify-center gap-2.5 rounded-lg border border-[#a6a6a6] bg-black px-3 text-left text-white transition-transform hover:scale-[1.02] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <GooglePlayIcon className="h-7 w-7 shrink-0" />
              <span className="flex flex-col">
                <span className="whitespace-nowrap text-[8px] font-medium uppercase leading-none tracking-[0.06em]">
                  Get it on
                </span>
                <span className="mt-1 whitespace-nowrap text-[14px] font-medium leading-none tracking-tight">
                  Google Play
                </span>
              </span>
            </a>
          </div>
        </div>
      </section>
    </main>
  )
}
